// components/agent/AgentInvitations.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Send, RefreshCw, User, Mail, Phone, Heart, ChevronDown, ChevronUp, MessageCircle, Package, Inbox, Eye, Calendar, X, Clock, Plus, Trash2, Check, Search, Upload, ZoomIn, ZoomOut } from 'react-feather';
import Cropper from 'react-easy-crop';
import AuthService from '../../services/authService';
import propertyService from '../../services/propertyService';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import BuyerDetailsModal from '../common/BuyerDetailsModal';
import CurateMatchesModal from '../common/CurateMatchesModal';
import '../admin/AdminDashboard.css';
import './AgentDashboard.css';

const AgentInvitations = ({ agentInfo }) => {
  const [invitations, setInvitations] = useState([]);
  const [assignedBuyers, setAssignedBuyers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [portalTarget, setPortalTarget] = useState(null);
  useEffect(() => {
    setPortalTarget(document.body);
  }, []);
  const [isBuyersLoading, setIsBuyersLoading] = useState(true);
  const [error, setError] = useState('');
  const [buyersError, setBuyersError] = useState('');
  const [formData, setFormData] = useState({
    recipientName: '',
    recipientEmail: '',
    recipientPhone: '',
    message: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null); // Track which buyer's modal is open
  const [showLikesModal, setShowLikesModal] = useState(false); // Control modal visibility
  const [resendingInvite, setResendingInvite] = useState(null); // Track which invite is being resent
  const [showInviteModal, setShowInviteModal] = useState(false); // Control invite form modal
  const [activeTableTab, setActiveTableTab] = useState('buyers'); // Toggle between invitations and buyers
  const [showDetailsModal, setShowDetailsModal] = useState(false); // Control property details modal
  const [selectedProperty, setSelectedProperty] = useState(null); // Property to show in details modal
  const [showBuyerDetailsModal, setShowBuyerDetailsModal] = useState(false); // Control buyer details modal
  const [selectedBuyerForDetails, setSelectedBuyerForDetails] = useState(null); // Buyer to show in details modal
  
  // Referral link and QR code state
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoadingQR, setIsLoadingQR] = useState(false);
  const [qrError, setQrError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  // PHOTO UPLOAD STATE
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [localProfileImage, setLocalProfileImage] = useState(null);

  // MESSAGING STATE - Agent messaging with users
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedUserForMessage, setSelectedUserForMessage] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  // CURATED MATCHES STATE (parent owns only the open/close + which buyer)
  const [showCurateModal, setShowCurateModal] = useState(false);
  const [selectedUserForCurate, setSelectedUserForCurate] = useState(null);
  const [isLightMode, setIsLightMode] = useState(true);

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  // Fetch invitations when component mounts
  useEffect(() => {
    fetchInvitations();
    fetchAssignedBuyers();
    fetchAnalytics();
  }, []);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setIsLoadingAnalytics(true);
      setAnalyticsError('');

      // Get current agent's profile to get their ID
      const profileResponse = await fetch('https://back-end-x7tx.onrender.com/api/users/profile', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch agent profile');
      }

      const profileData = await profileResponse.json();
      
      if (!profileData.success) {
        throw new Error('Failed to get agent ID');
      }

      const agentId = profileData.data._id;

      // Fetch referral links for this agent (same route as CreateAgentForm)
      const referralResponse = await fetch(`https://back-end-x7tx.onrender.com/api/referrals/agent/${agentId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!referralResponse.ok) {
        throw new Error('Failed to fetch referral data');
      }

      const referralLinks = await referralResponse.json();
      
      // Calculate totals from the referral links
      const totalClicks = referralLinks.reduce((sum, link) => sum + (link.stats?.clicks || 0), 0);
      const totalRegistrations = referralLinks.reduce((sum, link) => sum + (link.stats?.registrations || 0), 0);
      const totalLinks = referralLinks.length;
      
      // Calculate conversion rate
      let conversionRate = 0;
      if (totalClicks > 0) {
        conversionRate = ((totalRegistrations / totalClicks) * 100).toFixed(2);
      }
      
      // Format the data to match the expected structure
      const analyticsData = {
        summary: {
          totalClicks,
          totalRegistrations,
          totalLinks,
          conversionRate
        }
      };
      
      setAnalyticsData(analyticsData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsError('Failed to load analytics data');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHOTO UPLOAD HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handlePhotoClick = () => {
    setShowPhotoModal(true);
    setPhotoError('');
    setPhotoPreview(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Image must be less than 10MB');
      return;
    }

    setPhotoError('');
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageToCrop(e.target.result);
      setShowCropper(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleCropConfirm = async () => {
    try {
      const croppedBlob = await createCroppedImage(imageToCrop, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });
      
      const previewUrl = URL.createObjectURL(croppedBlob);
      
      setPhotoPreview({
        file: croppedFile,
        dataUrl: previewUrl
      });
      setShowCropper(false);
      setImageToCrop(null);
    } catch (error) {
      console.error('Error cropping image:', error);
      setPhotoError('Failed to crop image. Please try again.');
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview?.file) {
      setPhotoError('Please select an image first');
      return;
    }

    setIsUploadingPhoto(true);
    setPhotoError('');

    try {
      const formData = new FormData();
      formData.append('file', photoPreview.file);
      formData.append('upload_preset', 'lauft_profiles');
      formData.append('folder', 'profiles');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/dckv4itgt/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to cloud storage');
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const imageUrl = cloudinaryData.secure_url;

      const response = await fetch('https://back-end-x7tx.onrender.com/api/users/profile/image', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ profileImage: imageUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile image');
      }

      // Update local state to show the new image immediately
      setLocalProfileImage(imageUrl);
      
      setShowPhotoModal(false);
      setPhotoPreview(null);
      
      console.log('Profile image updated successfully');

    } catch (error) {
      console.error('Error uploading profile image:', error);
      setPhotoError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setPhotoPreview(null);
    setPhotoError('');
    setIsDragging(false);
    setShowCropper(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const fetchInvitations = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch('https://back-end-x7tx.onrender.com/api/invitations', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setInvitations(data.data);
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignedBuyers = async () => {
    try {
      setIsBuyersLoading(true);
      setBuyersError('');

      // Get current agent's profile to get their ID
      const profileResponse = await fetch('https://back-end-x7tx.onrender.com/api/users/profile', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch agent profile');
      }

      const profileData = await profileResponse.json();
      
      if (!profileData.success) {
        throw new Error('Failed to get agent ID');
      }

      const agentId = profileData.data._id;

      // Fetch assigned buyers for this agent
      const buyersResponse = await fetch(`https://back-end-x7tx.onrender.com/api/users/agent/${agentId}/buyers`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!buyersResponse.ok) {
        throw new Error('Failed to fetch assigned buyers');
      }

      const buyersData = await buyersResponse.json();
      
      if (buyersData.success && buyersData.data) {
        setAssignedBuyers(buyersData.data);
      } else {
        setAssignedBuyers([]);
      }
    } catch (error) {
      console.error('Error fetching assigned buyers:', error);
      setBuyersError('Failed to load assigned buyers. Please try again later.');
    } finally {
      setIsBuyersLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'recipientPhone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format the phone number
      let formattedPhone = '';
      if (digitsOnly.length <= 3) {
        formattedPhone = digitsOnly;
      } else if (digitsOnly.length <= 6) {
        formattedPhone = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
      } else {
        formattedPhone = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      }
      
      // Update form data with formatted phone
      setFormData({
        ...formData,
        [name]: formattedPhone
      });
    } else {
      // Handle other inputs normally
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccessMessage('');
    
    // Form validation
    if (!formData.recipientName.trim()) {
      setError("Recipient's name is required");
      return;
    }
    
    if (!formData.recipientEmail.trim()) {
      setError("Recipient's email is required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          recipientName: formData.recipientName.trim(),
          recipientEmail: formData.recipientEmail.trim() || undefined,
          recipientPhone: formData.recipientPhone.trim() || undefined,
          message: formData.message.trim()
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage('Invitation sent successfully!');
        // Reset form
        setFormData({
          recipientName: '',
          recipientEmail: '',
          recipientPhone: '',
          message: ''
        });
        // Refresh invitation list
        fetchInvitations();
      } else {
        setError(data.error || 'Failed to send invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setError('An error occurred while sending the invitation. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Contact buyer via email
  const contactBuyer = (buyer) => {
    if (buyer.email) {
      const subject = `Regarding Your Property Search - ${agentInfo?.name || 'Your Agent'}`;
      const body = `Hi ${buyer.name},\n\nI'm reaching out regarding your property search. I'd love to help you find your perfect home!\n\nBest regards,\n${agentInfo?.name || 'Your Agent'}`;
      window.location.href = `mailto:${buyer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  // Open modal with buyer's liked properties
  const openLikesModal = (buyer) => {
    setSelectedBuyer(buyer);
    setShowLikesModal(true);
  };

  // Close modal
  const closeLikesModal = () => {
    setShowLikesModal(false);
    setSelectedBuyer(null);
  };

  // Open property details modal (closes likes modal)
  const openPropertyDetails = (property) => {
    setSelectedProperty(property);
    setShowDetailsModal(true);
    setShowLikesModal(false); // Close likes modal when opening property details
  };

  // Close property details modal
  const closePropertyDetails = () => {
    setShowDetailsModal(false);
    setSelectedProperty(null);
  };

  // MESSAGING FUNCTIONS - Agent messaging with users
  const openMessageModal = async (buyer) => {
    setSelectedUserForMessage(buyer);
    setShowMessageModal(true);
    await startConversation(buyer);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
    setSelectedUserForMessage(null);
    setSelectedConversation(null);
    setMessages([]);
    setMessageText('');
  };

  // Start or load agent conversation with user
  const startConversation = async (user) => {
    try {
      // Check if AGENT conversation already exists with this user
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Look for existing agent conversation with this specific user
        const existing = data.data.find(c => 
          c.user._id === user._id && c.helperType === 'agent'
        );
        
        if (existing) {
          loadConversation(existing._id);
          return;
        }

        // Create new agent conversation
        const createResponse = await fetch(`${API_URL}/messages/conversations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({
            userId: user._id,
            subject: `Message from ${agentInfo?.name || 'Your Agent'}`
          })
        });

        const createData = await createResponse.json();
        
        if (createData.success) {
          loadConversation(createData.data._id);
        } else {
          setError(createData.error || 'Failed to create conversation');
        }
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
    }
  };

  // Load conversation messages
  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages || []);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  // Send message in agent conversation
  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          content: messageText.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages([...messages, data.data]);
        setMessageText('');
        setTimeout(scrollToBottom, 100);
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Error sending message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Resend invitation (reuses the create invitation endpoint)
  const resendInvitation = async (invitation) => {
    setResendingInvite(invitation._id);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          recipientName: invitation.recipientName,
          recipientEmail: invitation.recipientEmail || undefined,
          recipientPhone: invitation.recipientPhone || undefined,
          message: invitation.message || ''
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccessMessage(`Invitation resent to ${invitation.recipientName}!`);
        // Refresh invitations list
        fetchInvitations();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError(data.error || 'Failed to resend invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('An error occurred while resending the invitation. Please try again later.');
    } finally {
      setResendingInvite(null);
    }
  };

  // Format date to display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format phone number to (xxx) xxx-xxxx
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  };

  // Fetch and display referral link modal
  const handleOpenReferralModal = async () => {
    setShowReferralModal(true);
    setIsLoadingReferral(true);
    setReferralError('');
    setCopySuccess(false);

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/agents/me/referral-link', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setReferralLink(data.data.referralLink);
      } else {
        setReferralError(data.error || 'Failed to fetch referral link');
      }
    } catch (error) {
      console.error('Error fetching referral link:', error);
      setReferralError('An error occurred while fetching your referral link');
    } finally {
      setIsLoadingReferral(false);
    }
  };

  // Copy referral link to clipboard
  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please try again.');
    });
  };

  // Open QR code modal and fetch QR code
  const handleOpenQRModal = async () => {
    setShowQRModal(true);
    setIsLoadingQR(true);
    setQrError('');

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/agents/me/qr-code', {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQrCodeUrl(data.data.qrCodeUrl);
      } else {
        setQrError(data.error || 'Failed to fetch QR code');
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
      setQrError('An error occurred while fetching your QR code');
    } finally {
      setIsLoadingQR(false);
    }
  };

  // Download QR code from modal
  const handleDownloadQRCode = async () => {
    try {
      // Fetch the image as a blob
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${agentInfo?.name || 'Agent'}_QR_Code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      setSuccessMessage('QR code downloaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setError('Failed to download QR code. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Get status badge class name
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent':
        return 'status-badge status-sent';
      case 'accepted':
        return 'status-badge status-accepted';
      default:
        return 'status-badge status-pending';
    }
  };

  // CURATED MATCHES — modal owns its own state; parent only opens/closes and provides the buyer
  const openCurateModal = (buyer) => {
    setSelectedUserForCurate(buyer);
    setShowCurateModal(true);
  };

  const closeCurateModal = () => {
    setShowCurateModal(false);
    setSelectedUserForCurate(null);
  };


  return (
    <>
      {/* Global Styles for Table Badges */}
      {/* Global Styles for Table Badges */}
      <style>{`
        /* Table stat badges — modern light */
        .table-stat-badge {
          display: inline-block;
          background-color: rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.08);
          color: #111 !important;
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 0.82rem;
          font-weight: 600;
          transition: all 0.15s;
        }
        .table-stat-badge:hover {
          background-color: rgba(0, 0, 0, 0.08);
          border-color: rgba(0, 0, 0, 0.15);
        }
        .table-stat-badge.light-mode-badge {
          background-color: rgba(0, 0, 0, 0.04) !important;
          border: 1px solid rgba(0, 0, 0, 0.08) !important;
          color: #111 !important;
        }

        /* Buyer action pill buttons — dark chip, white text+icons */
        .buyer-action-btn {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
          transition: all 0.15s ease;
        }
        .buyer-action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
        }
        .buyer-action-btn,
        .buyer-action-btn span {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        .buyer-action-btn svg,
        .buyer-action-btn svg line,
        .buyer-action-btn svg polyline,
        .buyer-action-btn svg path,
        .buyer-action-btn svg circle,
        .buyer-action-btn svg rect {
          stroke: #ffffff !important;
          color: #ffffff !important;
        }
      `}</style>


      {/* Success/Error Messages */}
      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <p>{successMessage}</p>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          BENTO GRID HERO SECTION
          ═══════════════════════════════════════════════════════════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 220px',
        gridTemplateRows: 'auto auto',
        gap: '16px',
        marginBottom: '32px'
      }}>
        
        {/* ─────────────────────────────────────────────────────────────
            AGENT PROFILE CARD (Top Left)
            ───────────────────────────────────────────────────────────── */}
        <div style={{
          gridRow: '1 / 2',
          backgroundColor: '#ffffff',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: '16px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Card Header */}
          <span style={{
            color: '#9ca3af',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '500'
          }}>
            Agent Profile
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Profile Photo with Dark Grey Ring - Clickable to upload */}
            <div 
              onClick={handlePhotoClick}
              style={{
                position: 'relative',
                flexShrink: 0,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, opacity 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.03)';
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.opacity = '1';
              }}
            >
              <div style={{
                width: '88px',
                height: '88px',
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.08)',
                padding: '3px',
                boxShadow: '0 0 16px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  padding: '3px',
                  overflow: 'hidden'
                }}>
                  {(localProfileImage || agentInfo?.profileImage) && (localProfileImage || agentInfo.profileImage) !== 'default-profile.jpg' ? (
                    <img 
                      src={localProfileImage || agentInfo.profileImage}
                      alt={agentInfo?.name || 'Agent'}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={36} color="#9ca3af" />
                    </div>
                  )}
                </div>
              </div>
              {/* Online indicator dot */}
              <div style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                border: '3px solid #1a1a1a',
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)'
              }} />
            </div>
          
          {/* Profile Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              color: '#1a1a1a',
              fontSize: '1.35rem',
              fontWeight: '700',
              margin: '0 0 12px 0',
              letterSpacing: '-0.01em',
              textAlign: 'left'
            }}>
              {agentInfo?.name || 'Agent'}
            </h2>
            
            {/* Contact Details - 2x2 Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Row 1: Phone | Brokerage */}
              <div style={{ display: 'flex', gap: '32px' }}>
                {/* Phone */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minWidth: '180px'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Phone size={14} color="#1a1a1a" />
                  </div>
                  <span style={{
                    color: '#1a1a1a',
                    fontSize: '0.9rem'
                  }}>
                    {agentInfo?.phone ? formatPhoneNumber(agentInfo.phone) : 'No phone set'}
                  </span>
                </div>

                {/* Brokerage */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                      <line x1="9" y1="6" x2="15" y2="6"></line>
                      <line x1="9" y1="10" x2="15" y2="10"></line>
                      <line x1="9" y1="14" x2="15" y2="14"></line>
                      <line x1="9" y1="18" x2="15" y2="18"></line>
                    </svg>
                  </div>
                  <span style={{
                    color: '#1a1a1a',
                    fontSize: '0.9rem'
                  }}>
                    {agentInfo?.agentInfo?.agency || 'No brokerage'}
                  </span>
                </div>
              </div>

              {/* Row 2: Email | License */}
              <div style={{ display: 'flex', gap: '32px' }}>
                {/* Email */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minWidth: '180px'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Mail size={14} color="#1a1a1a" />
                  </div>
                  <span style={{
                    color: '#1a1a1a',
                    fontSize: '0.9rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {agentInfo?.agentInfo?.aliasEmail || agentInfo?.email || 'No email set'}
                  </span>
                </div>

                {/* License Number */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                      <line x1="7" y1="8" x2="17" y2="8"></line>
                      <line x1="7" y1="12" x2="13" y2="12"></line>
                    </svg>
                  </div>
                  <span style={{
                    color: '#1a1a1a',
                    fontSize: '0.9rem'
                  }}>
                    {agentInfo?.agentInfo?.licenseNumber || 'No license #'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ─────────────────────────────────────────────────────────────
            STATS COLUMN (Right Side - Stacked)
            ───────────────────────────────────────────────────────────── */}
        <div style={{
          gridRow: '1 / 3',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {/* Clicks Stat */}
          <div style={{
            flex: 1,
            backgroundColor: '#ffffff',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background icon - blurred cursor/click */}
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              opacity: 0.08,
              pointerEvents: 'none',
              filter: 'blur(1px)'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
            </div>
            
            {isLoadingAnalytics ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div className="skeleton skeleton-text-lg" style={{ width: '50px', height: '32px' }}></div>
                <div className="skeleton skeleton-text-sm" style={{ width: '40px', height: '12px' }}></div>
              </div>
            ) : (
              <>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  lineHeight: 1,
                  marginBottom: '4px'
                }}>
                  {analyticsData?.summary?.totalClicks || 0}
                </div>
                <span style={{
                  color: '#9ca3af',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '500'
                }}>
                  Clicks
                </span>
              </>
            )}
          </div>

          {/* Conversions Stat */}
          <div style={{
            flex: 1,
            backgroundColor: '#ffffff',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background icon - blurred user check */}
            <div style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              opacity: 0.08,
              pointerEvents: 'none',
              filter: 'blur(1px)'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <polyline points="17 11 19 13 23 9"></polyline>
              </svg>
            </div>
            
            {isLoadingAnalytics ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <div className="skeleton skeleton-text-lg" style={{ width: '50px', height: '32px' }}></div>
                <div className="skeleton skeleton-text-sm" style={{ width: '55px', height: '12px' }}></div>
              </div>
            ) : (
              <>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  lineHeight: 1,
                  marginBottom: '4px'
                }}>
                  {analyticsData?.summary?.totalRegistrations || 0}
                </div>
                <span style={{
                  color: '#9ca3af',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '500'
                }}>
                  Converts
                </span>
              </>
            )}
          </div>
        </div>

        {/* ─────────────────────────────────────────────────────────────
            ACTIONS ROW (Bottom Left)
            ───────────────────────────────────────────────────────────── */}
        <div style={{
          gridRow: '2 / 3',
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '12px'
        }}>
          {/* Invite User Button - Primary Action */}
          <button
            onClick={() => setShowInviteModal(true)}
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '16px 20px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Plus size={18} color="#111" />
            <span style={{
              color: '#111',
              fontSize: '0.95rem',
              fontWeight: '600'
            }}>
              Invite New User
            </span>
          </button>

          {/* Copy Link Button - Icon Only */}
          <button
            onClick={handleOpenReferralModal}
            title="Copy Referral Link"
            style={{
              width: '52px',
              height: '52px',
              backgroundColor: '#ffffff',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </button>

          {/* QR Code Button - Icon Only */}
          <button
            onClick={handleOpenQRModal}
            title="View QR Code"
            style={{
              width: '52px',
              height: '52px',
              backgroundColor: '#ffffff',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </button>
        </div>
      </div>


      {/* Invite Modal */}
      {showInviteModal && (
        <div 
          onClick={() => setShowInviteModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: isLightMode ? '#ffffff' : '#1a1a1a',
              border: isLightMode ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              padding: '0',
              maxWidth: '480px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '28px 32px 0 32px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div>
                <h2 style={{
                  color: isLightMode ? '#1a1a1a' : '#ffffff',
                  fontSize: '1.4rem',
                  fontWeight: '700',
                  margin: '0 0 4px 0',
                  letterSpacing: '-0.02em',
                  textAlign: 'left',
                }}>
                  Invite a User
                </h2>
                <p style={{
                  color: isLightMode ? '#ffffff' : '#9ca3af',
                  fontSize: '0.85rem',
                  margin: 0,
                }}>
                  Send a personalized invitation to join Lauft
                </p>
              </div>
              <div
                onClick={() => setShowInviteModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                  transition: 'background-color 0.2s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isLightMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.04)'; }}
              >
                <X size={16} color={isLightMode ? '#1a1a1a' : '#ffffff'} />
              </div>
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              margin: '20px 32px 0 32px',
            }} />

            {/* Form */}
            <form onSubmit={(e) => {
              handleSubmit(e);
              setShowInviteModal(false);
            }} style={{ padding: '24px 32px 28px 32px' }}>
              
              {/* Name Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: isLightMode ? '#6b7280' : '#ffffff',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  <User size={14} />
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleInputChange}
                  placeholder="Enter recipient's full name"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: isLightMode ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(99, 102, 241, 0.5)'; }}
                  onBlur={(e) => { e.target.style.borderColor = isLightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)'; }}
                />
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: isLightMode ? '#6b7280' : '#ffffff',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  <Mail size={14} />
                  Email Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="email"
                  name="recipientEmail"
                  value={formData.recipientEmail}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: isLightMode ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(99, 102, 241, 0.5)'; }}
                  onBlur={(e) => { e.target.style.borderColor = isLightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)'; }}
                />
              </div>

              {/* Phone Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: isLightMode ? '#6b7280' : '#ffffff',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  <Phone size={14} />
                  Phone Number <span style={{ fontWeight: '400', textTransform: 'none', opacity: 0.6, fontSize: '0.7rem' }}>(optional)</span>
                </label>
                <input
                  type="tel"
                  name="recipientPhone"
                  value={formData.recipientPhone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: isLightMode ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(99, 102, 241, 0.5)'; }}
                  onBlur={(e) => { e.target.style.borderColor = isLightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)'; }}
                />
              </div>

              {/* Message Field */}
              <div style={{ marginBottom: '28px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: isLightMode ? '#6b7280' : '#ffffff',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Custom Message <span style={{ fontWeight: '400', textTransform: 'none', opacity: 0.6, fontSize: '0.7rem' }}>(optional)</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add a personal touch to your invitation..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: isLightMode ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)',
                    backgroundColor: isLightMode ? 'rgba(0, 0, 0, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                    color: isLightMode ? '#1a1a1a' : '#ffffff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    minHeight: '80px',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(99, 102, 241, 0.5)'; }}
                  onBlur={(e) => { e.target.style.borderColor = isLightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)'; }}
                />
              </div>

              {/* Submit Button */}
              <div
                className="buyer-action-btn"
                onClick={(e) => {
                  if (!isSubmitting) {
                    e.preventDefault();
                    const form = e.currentTarget.closest('form');
                    form.requestSubmit();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '12px',
                  backgroundColor: '#374151',
                  color: '#1a1a1a',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.6 : 1,
                  transition: 'all 0.2s',
                  userSelect: 'none',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.backgroundColor = '#4b5563'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#374151'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.15)'; }}
              >
                {isSubmitting ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send size={16} />
                    Send Invitation
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Referral Link Modal */}
      {showReferralModal && (
        <div 
          className="modal-overlay"
          onClick={() => setShowReferralModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                color: '#1a1a1a',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0
              }}>
                Your Referral Link
              </h2>
              <button
                onClick={() => setShowReferralModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {isLoadingReferral ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#6b7280'
              }}>
                <div className="spinner-small" style={{ margin: '0 auto 16px' }}></div>
                Loading your referral link...
              </div>
            ) : referralError ? (
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                borderRadius: '8px',
                color: '#ff6b6b',
                marginBottom: '16px'
              }}>
                {referralError}
              </div>
            ) : (
              <>
                <p style={{
                  color: '#6b7280',
                  marginBottom: '16px',
                  fontSize: '0.95rem'
                }}>
                  Share this link with potential buyers to track referrals:
                </p>
                
                <div style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <code style={{
                    flex: 1,
                    color: '#1a1a1a',
                    fontSize: '0.95rem',
                    wordBreak: 'break-all',
                    backgroundColor: 'transparent'
                  }}>
                    {referralLink}
                  </code>
                </div>

                <button
                  onClick={handleCopyReferralLink}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    backgroundColor: copySuccess ? 'rgba(40, 167, 69, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                    border: copySuccess ? '1px solid rgba(40, 167, 69, 0.5)' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!copySuccess) {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!copySuccess) {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.06)';
                    }
                  }}
                >
                  {copySuccess ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Copied to Clipboard!
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                      Copy Link
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div 
          className="modal-overlay"
          onClick={() => setShowQRModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                color: '#1a1a1a',
                fontSize: '1.5rem',
                fontWeight: '700',
                margin: 0
              }}>
                Your QR Code
              </h2>
              <button
                onClick={() => setShowQRModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {isLoadingQR ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 0',
                color: '#6b7280'
              }}>
                <div className="spinner-small" style={{ margin: '0 auto 16px' }}></div>
                Loading your QR code...
              </div>
            ) : qrError ? (
              <div style={{
                padding: '20px',
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                borderRadius: '8px',
                color: '#ff6b6b',
                marginBottom: '16px'
              }}>
                {qrError}
              </div>
            ) : (
              <>
                <p style={{
                  color: '#6b7280',
                  marginBottom: '16px',
                  fontSize: '0.95rem',
                  textAlign: 'center'
                }}>
                  Share this QR code with potential buyers - they can scan it to access your referral link!
                </p>
                
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <img 
                    src={qrCodeUrl} 
                    alt="Agent QR Code"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>

                <button
                  onClick={handleDownloadQRCode}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    color: '#1a1a1a',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.06)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download QR Code
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabbed Table Section */}
      <div style={{
        backgroundColor: '#ffffff',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Tab Headers */}
        <div style={{
          display: 'flex',
          gap: '16px',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '24px'
        }}>
          <button
            onClick={() => setActiveTableTab('buyers')}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: activeTableTab === 'buyers' ? '#111' : '#6b7280',
              border: 'none',
              borderBottom: activeTableTab === 'buyers' ? '2px solid #111' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              transition: 'all 0.15s',
              marginBottom: '-1px',
              letterSpacing: '-0.005em'
            }}
          >
            Assigned Users
          </button>
          <button
            onClick={() => setActiveTableTab('invitations')}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: activeTableTab === 'invitations' ? '#111' : '#6b7280',
              border: 'none',
              borderBottom: activeTableTab === 'invitations' ? '2px solid #111' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              transition: 'all 0.15s',
              marginBottom: '-1px',
              letterSpacing: '-0.005em'
            }}
          >
            Pending Invites
          </button>
        </div>

        {/* Pending Invitations Table */}
        {activeTableTab === 'invitations' && (
          <>
            {isLoading ? (
              <div style={{ padding: '0' }}>
                {/* Skeleton table header */}
                <div style={{ 
                  display: 'flex', 
                  padding: '16px', 
                  borderBottom: '2px solid rgba(0, 0, 0, 0.06)',
                  gap: '16px'
                }}>
                  <div className="skeleton" style={{ width: '120px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '100px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '80px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '60px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '70px', height: '14px' }}></div>
                </div>
                {/* Skeleton rows */}
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton-row">
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '140px', height: '16px' }}></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '180px', height: '14px', marginBottom: '6px' }}></div>
                      <div className="skeleton" style={{ width: '120px', height: '14px' }}></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '80px', height: '14px' }}></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton skeleton-badge"></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton skeleton-button"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : invitations.length > 0 ? (
              <div className="table-responsive" style={{ backgroundColor: 'transparent', border: 'none', padding: 0, color: '#1a1a1a' }}>
                <table className="data-table" style={{ backgroundColor: 'transparent', color: '#1a1a1a' }}>
                  <thead>
                    <tr>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Recipient</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Contact</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Sent Date</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Status</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Actions</th>
                    </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr 
                    key={invitation._id}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    style={{ backgroundColor: 'transparent', transition: 'background-color 0.2s' }}
                  >
                    <td style={{ color: '#1a1a1a' }}>
                      <strong style={{ color: '#1a1a1a' }}>{invitation.recipientName}</strong>
                    </td>
                    <td style={{ color: '#1a1a1a' }}>
                      <div className="contact-details" style={{ color: '#1a1a1a' }}>
                        {invitation.recipientEmail && (
                          <div className="contact-item" style={{ color: '#1a1a1a' }}>
                            <Mail size={14} style={{ color: '#1a1a1a' }} />
                            <span style={{ color: '#1a1a1a' }}>{invitation.recipientEmail}</span>
                          </div>
                        )}
                        {invitation.recipientPhone && (
                          <div className="contact-item" style={{ color: '#1a1a1a' }}>
                            <Phone size={14} style={{ color: '#1a1a1a' }} />
                            <span style={{ color: '#1a1a1a' }}>{invitation.recipientPhone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ color: '#1a1a1a' }}>{formatDate(invitation.createdAt)}</td>
                    <td style={{ color: '#1a1a1a' }}>
                      <span className={getStatusBadgeClass(invitation.status)} style={{ color: 'rgba(0, 0, 0, 0.9)' }}>
                        {invitation.status || 'pending'}
                      </span>
                    </td>
                    <td style={{ color: '#1a1a1a' }}>
                      <button
                        onClick={() => resendInvitation(invitation)}
                        className="btn btn-sm btn-secondary resend-btn"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          color: '#1a1a1a',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        disabled={resendingInvite === invitation._id}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                        }}
                      >
                        {resendingInvite === invitation._id ? (
                          <>
                            <div className="loading-spinner-sm"></div>
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <RefreshCw size={14} />
                            <span>Resend</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" style={{ 
            backgroundColor: 'transparent',
            color: '#6b7280',
            padding: '48px',
            textAlign: 'center'
          }}>
            <Send size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
            <p style={{ color: '#1a1a1a', fontSize: '1.1rem', marginBottom: '8px' }}>No invitations sent yet.</p>
            <p style={{ color: '#6b7280' }}>Start by sending your first buyer invitation above.</p>
          </div>
        )}
        </>
        )}

        {/* Assigned Buyers Table */}
        {activeTableTab === 'buyers' && (
          <>
            {buyersError && (
              <div style={{
                backgroundColor: 'rgba(220, 38, 38, 0.15)',
                color: '#fca5a5',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0 }}>{buyersError}</p>
              </div>
            )}

            {isBuyersLoading ? (
              <div style={{ padding: '0' }}>
                {/* Skeleton table header */}
                <div style={{ 
                  display: 'flex', 
                  padding: '16px', 
                  borderBottom: '2px solid rgba(0, 0, 0, 0.06)',
                  gap: '16px'
                }}>
                  <div className="skeleton" style={{ width: '80px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '100px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '100px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '60px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '100px', height: '14px' }}></div>
                  <div className="skeleton" style={{ width: '70px', height: '14px' }}></div>
                </div>
                {/* Skeleton rows */}
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton-row" style={{ height: '110px', alignItems: 'center' }}>
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '120px', height: '16px' }}></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '120px', height: '14px', marginBottom: '6px' }}></div>
                      <div className="skeleton" style={{ width: '160px', height: '14px' }}></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton skeleton-badge"></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton skeleton-badge"></div>
                    </div>
                    <div className="skeleton-cell">
                      <div className="skeleton" style={{ width: '80px', height: '14px' }}></div>
                    </div>
                    <div className="skeleton-cell" style={{ display: 'flex', gap: '8px' }}>
                      <div className="skeleton skeleton-button"></div>
                      <div className="skeleton skeleton-button"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : assignedBuyers.length > 0 ? (
              <div className="table-responsive" style={{ backgroundColor: 'transparent', border: 'none', padding: 0, color: '#1a1a1a' }}>
                <table className="data-table" style={{ backgroundColor: 'transparent', color: '#1a1a1a' }}>
                  <thead>
                    <tr>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>User</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>User Info</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Matches Sent</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Swipes</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Claimed Date</th>
                      <th style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedBuyers.map((buyer) => (
                      <tr 
                        key={buyer._id}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        style={{ backgroundColor: 'transparent', transition: 'background-color 0.2s', borderBottom: '1px solid rgba(0, 0, 0, 0.06)', height: '110px' }}
                      >
                        <td style={{ color: '#1a1a1a', textAlign: 'left', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>
                          <div className="buyer-info" style={{ color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div 
                              onClick={() => {
                                setSelectedBuyerForDetails(buyer);
                                setShowBuyerDetailsModal(true);
                              }}
                              style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              flexShrink: 0,
                              cursor: 'pointer',
                              background: buyer.profileImage && buyer.profileImage !== 'default-profile.jpg' && buyer.profileImage.length > 5
                                ? 'transparent'
                                : 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid rgba(0, 0, 0, 0.08)',
                            }}>
                              {buyer.profileImage && buyer.profileImage !== 'default-profile.jpg' && buyer.profileImage.length > 5 ? (
                                <img 
                                  src={buyer.profileImage} 
                                  alt={buyer.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.style.background = 'linear-gradient(135deg, #4b5563 0%, #374151 100%)';
                                    e.target.insertAdjacentHTML('afterend', '<svg viewBox="0 0 24 24" fill="none" style="width:60%;height:60%"><circle cx="12" cy="8" r="4" fill="#9ca3af"/><path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" fill="#9ca3af"/></svg>');
                                  }}
                                />
                              ) : (
                                <svg viewBox="0 0 24 24" fill="none" style={{ width: '60%', height: '60%' }}>
                                  <circle cx="12" cy="8" r="4" fill="#9ca3af" />
                                  <path d="M4 20c0-3.3 2.7-6 6-6h4c3.3 0 6 2.7 6 6" fill="#9ca3af" />
                                </svg>
                              )}
                            </div>
                            <strong 
                              style={{ 
                                color: '#1a1a1a',
                                cursor: 'pointer',
                                transition: 'color 0.2s'
                              }}
                              onClick={() => {
                                setSelectedBuyerForDetails(buyer);
                                setShowBuyerDetailsModal(true);
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#6b7280'}
                              onMouseLeave={(e) => e.target.style.color = '#1a1a1a'}
                            >
                              {buyer.name}
                            </strong>
                          </div>
                        </td>
                        <td style={{ color: '#1a1a1a', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>
                          <div className="contact-details" style={{ color: '#1a1a1a', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div 
                              onClick={() => {
                                setSelectedBuyerForDetails(buyer);
                                setShowBuyerDetailsModal(true);
                              }}
                              style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              backgroundColor: 'rgba(99, 102, 241, 0.15)',
                              border: '1px solid rgba(99, 102, 241, 0.3)',
                              padding: '3px 10px',
                              borderRadius: '6px',
                              width: 'fit-content',
                              marginBottom: '2px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.25)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.15)'; }}
                            >
                              <span style={{ fontSize: '0.75rem' }}>
                                {{ flex: '⚪', bronze: '🥉', silver: '🥈', gold: '🥇', platinum: '💎' }[buyer.leadTier?.toLowerCase()] || '⭐'}
                              </span>
                              <span style={{
                                color: '#818cf8',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                              }}>
                                {buyer.leadTier || 'Unassigned'}
                              </span>
                            </div>
                            {buyer.phone && (
                              <div className="contact-item" style={{ color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Phone size={14} style={{ color: '#1a1a1a' }} />
                                <span style={{ color: '#1a1a1a' }}>{formatPhoneNumber(buyer.phone)}</span>
                              </div>
                            )}
                            {buyer.email && (
                              <div className="contact-item" style={{ color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Mail size={14} style={{ color: '#1a1a1a' }} />
                                <span style={{ color: '#1a1a1a' }}>{buyer.email}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ color: '#1a1a1a', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>
                          <div 
                            className={`table-stat-badge ${isLightMode ? 'light-mode-badge' : ''}`}
                            onClick={() => {
                              setSelectedBuyerForDetails(buyer);
                              setShowBuyerDetailsModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {buyer.curatedMatchesCount || 0}
                          </div>
                        </td>
                        <td style={{ color: '#1a1a1a', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>
                          <div 
                            className={`table-stat-badge ${isLightMode ? 'light-mode-badge' : ''}`}
                            onClick={() => {
                              setSelectedBuyerForDetails(buyer);
                              setShowBuyerDetailsModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {(buyer.likes?.length || 0) + (buyer.dislikes?.length || 0)}
                          </div>
                        </td>
                        <td style={{ color: '#1a1a1a', fontSize: '0.7rem', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>{formatDate(buyer.assignedAt)}</td>
                        <td style={{ color: '#1a1a1a', paddingTop: '36px', paddingBottom: '36px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div
                              onClick={() => openMessageModal(buyer)}
                              className="buyer-action-btn"
                              style={{
                              backgroundColor: isLightMode ? '#374151' : '#374151',
                              border: isLightMode ? '1px solid #4b5563' : '1px solid rgba(0, 0, 0, 0.06)',
                              color: '#1a1a1a',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              userSelect: 'none',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#4b5563';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#374151';
                            }}
                          >
                            <MessageCircle size={14} color="#ffffff" />
                            <span style={{ color: '#1a1a1a' }}>Message</span>
                          </div>
                          <div
                            onClick={() => openCurateModal(buyer)}
                            className="buyer-action-btn"
                            title="Curate Matches"
                            style={{
                              backgroundColor: isLightMode ? '#374151' : '#374151',
                              border: isLightMode ? '1px solid #4b5563' : '1px solid rgba(0, 0, 0, 0.06)',
                              color: '#1a1a1a',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              userSelect: 'none',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#4b5563';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#374151';
                            }}
                          >
                            <Inbox size={14} color="#ffffff" />
                            <span style={{ color: '#1a1a1a' }}>Matches</span>
                          </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{
                backgroundColor: 'transparent',
                color: '#6b7280',
                padding: '48px',
                textAlign: 'center'
              }}>
                <User size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                <p style={{ color: '#1a1a1a', fontSize: '1.1rem', marginBottom: '8px' }}>No buyers assigned to you yet.</p>
                <p style={{ color: '#6b7280' }}>Buyers will appear here when an admin assigns them to you or when they register through your invitation.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Liked Properties Modal */}
      {showLikesModal && selectedBuyer && (
        <div className="modal-overlay" onClick={closeLikesModal}>
          
          <style>{`
            /* Liked Properties Modal — data table, light */
            .data-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              color: #111 !important;
            }
            .data-table thead {
              background-color: #fafafa !important;
            }
            .data-table th {
              color: #6b7280 !important;
              border-bottom: 1px solid #e5e7eb !important;
              background-color: #fafafa !important;
              padding: 12px 16px !important;
              text-align: left;
              font-weight: 600;
              font-size: 0.7rem;
              text-transform: uppercase;
              letter-spacing: 0.06em;
            }
            .data-table tbody tr {
              background-color: transparent !important;
              transition: background-color 0.12s;
              border: none !important;
            }
            .data-table tbody tr:hover,
            .data-table tbody tr:hover td {
              background-color: #f9fafb !important;
            }
            .data-table td {
              color: #111 !important;
              border-bottom: 1px solid #f0f0f0 !important;
              background-color: transparent !important;
              padding: 14px 16px !important;
              border-left: none !important;
              border-right: none !important;
              border-top: none !important;
              font-size: 0.9rem;
            }
            .data-table tbody tr:last-child td {
              border-bottom: none !important;
            }
            .data-table a {
              color: #111 !important;
              text-decoration: underline;
              text-underline-offset: 2px;
            }
            .data-table a:hover {
              color: #000 !important;
            }
            .buyer-info strong {
              color: #111 !important;
              font-weight: 600;
            }
            .contact-details,
            .contact-item {
              color: #374151 !important;
              font-size: 0.82rem;
            }
            .contact-item a {
              color: #374151 !important;
            }
            .contact-item a:hover {
              color: #111 !important;
            }
            .text-text-medium,
            .showings-count {
              color: #6b7280 !important;
            }
            .activity-summary,
            .activity-stat {
              color: #374151 !important;
              font-size: 0.85rem;
            }
            .activity-stat strong {
              color: #111 !important;
            }
            .btn-link {
              color: #374151 !important;
              background: none !important;
              border: none !important;
              text-decoration: none !important;
              padding: 5px 10px !important;
              border-radius: 6px !important;
              font-weight: 500 !important;
              transition: all 0.12s !important;
            }
            .btn-link:hover {
              color: #111 !important;
              background-color: #f4f4f5 !important;
            }
            .btn-link:disabled {
              color: #9ca3af !important;
              cursor: not-allowed !important;
            }
            small, .text-small {
              color: #6b7280 !important;
            }
            .status-badge {
              display: inline-block;
              padding: 3px 10px;
              border-radius: 999px;
              font-size: 0.72rem;
              font-weight: 600;
              letter-spacing: 0.02em;
              text-transform: uppercase;
            }
            .status-badge.status-pending {
              background-color: #fff7ed;
              color: #c2410c;
              border: 1px solid #fed7aa;
            }
            .status-badge.status-registered {
              background-color: #ecfdf5;
              color: #047857;
              border: 1px solid #a7f3d0;
            }
            .loading-spinner, .spinner {
              border: 2.5px solid #f0f0f0 !important;
              border-top-color: #111 !important;
            }
          `}</style>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              borderRadius: '16px',
              maxWidth: '1000px',
              maxHeight: '85vh',
              overflow: 'hidden'
            }}
          >
            <div 
              className="modal-header"
              style={{
                backgroundColor: 'transparent',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                padding: '24px'
              }}
            >
              <h2 className="modal-title" style={{ color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
                <Heart size={24} style={{ color: '#FF6B6B' }} />
                {selectedBuyer.name}'s Liked Properties
              </h2>
              <button 
                onClick={closeLikesModal} 
                className="modal-close"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#111'}
                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '24px', overflowY: 'auto' }}>
              {selectedBuyer.likes && selectedBuyer.likes.length > 0 ? (
                <div className="modal-properties-grid">
                  {selectedBuyer.likes.map((property) => (
                    <div 
                      key={property._id} 
                      className="modal-property-card"
                      onClick={() => openPropertyDetails(property)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img 
                        src={property.images?.[0] || '/placeholder-house.jpg'} 
                        alt={property.address}
                        className="modal-property-image"
                      />
                      <div className="modal-property-info">
                        <div className="modal-property-address">{property.address}</div>
                        <div className="modal-property-price">
                          {property.price?.toLocaleString ? property.price.toLocaleString() : property.price}
                        </div>
                        <div className="modal-property-specs">
                          {property.bedrooms} bd | {property.bathrooms} ba | {property.squareFootage?.toLocaleString()} sqft
                        </div>
                        {property.neighborhood && (
                          <div className="modal-property-neighborhood">
                            {property.neighborhood}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="modal-empty" style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                  <Heart size={48} style={{ color: '#d1d5db' }} />
                  <p style={{ marginTop: '16px', fontSize: '1rem' }}>No liked properties yet</p>
                </div>
              )}
            </div>
            
            <div 
              className="modal-footer"
              style={{
                borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <button 
                onClick={closeLikesModal} 
                className="btn btn-secondary"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  color: '#1a1a1a',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.06)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Property Details Modal */}
      <PropertyDetailsModal 
        property={selectedProperty}
        isOpen={showDetailsModal}
        onClose={closePropertyDetails}
      />
      {/* Custom Styling */}
      <style jsx>{`
        /* Buyers section container — clean white card */
        .buyers-section-wrapper {
          position: relative;
          margin-bottom: 2rem;
          padding: 0;
          border-radius: 14px;
          overflow: visible;
          background: transparent;
        }

        /* Primary / secondary button styles */
        .btn-primary {
          background-color: #111;
          color: #ffffff;
          border: 1px solid #111;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.88rem;
          transition: all 0.12s;
          cursor: pointer;
        }
        .btn-primary:hover {
          background-color: #000;
          transform: translateY(-1px);
        }
        .btn-primary:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        /* Skeleton shimmer (light) */
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .skeleton {
          background: linear-gradient(90deg,
            rgba(0, 0, 0, 0.05) 25%,
            rgba(0, 0, 0, 0.1) 50%,
            rgba(0, 0, 0, 0.05) 75%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 6px;
        }
        .skeleton-text { height: 14px; margin-bottom: 8px; }
        .skeleton-text-lg { height: 32px; width: 60px; margin-bottom: 8px; }
        .skeleton-text-sm { height: 12px; width: 50px; }
        .skeleton-row {
          display: flex;
          align-items: center;
          padding: 20px 16px;
          border-bottom: 1px solid #f0f0f0;
          gap: 16px;
        }
        .skeleton-cell { flex: 1; }
        .skeleton-avatar { width: 40px; height: 40px; border-radius: 50%; }
        .skeleton-badge { width: 48px; height: 32px; border-radius: 6px; }
        .skeleton-button { width: 80px; height: 36px; border-radius: 8px; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Buyers section content card */
        .buyers-section {
          position: relative;
          z-index: 2;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
          border-radius: 14px;
          padding: 1.5rem 1.75rem;
        }

        .section-header-with-refresh {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.25rem;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 0;
          color: #111;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        /* Status badges (pills) */
        .status-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .status-pending {
          background-color: #f3f4f6;
          color: #4b5563;
          border: 1px solid #e5e7eb;
        }
        .status-sent {
          background-color: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
        }
        .status-accepted {
          background-color: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
        }
        .status-buy {
          background-color: #ecfdf5;
          color: #047857;
          border: 1px solid #a7f3d0;
        }
        .status-rent {
          background-color: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .contact-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.82rem;
          color: #374151;
        }
        .buyer-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        /* Link-style buttons (resend, cancel) */
        .btn-link {
          background: none;
          border: none;
          color: #374151;
          font-size: 0.85rem;
          cursor: pointer;
          padding: 5px 10px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          text-decoration: none;
          transition: all 0.12s;
          font-weight: 500;
        }
        .btn-link:hover {
          background-color: #f4f4f5;
          color: #111;
        }
        .btn-link:disabled {
          color: #9ca3af;
          cursor: not-allowed;
        }
        .btn-link:disabled:hover {
          background-color: transparent;
        }

        /* Modal Styles — modern light */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .modal-content {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #f0f0f0;
        }
        .modal-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #111;
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 0;
          letter-spacing: -0.01em;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #6b7280;
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.12s;
        }
        .modal-close:hover {
          background-color: #f4f4f5;
          color: #111;
        }
        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        /* Property cards inside curated-matches modal — light */
        .modal-properties-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }
        .modal-property-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
          cursor: pointer;
        }
        .modal-property-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
          border-color: #d1d5db;
        }
        .modal-property-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
        }
        .modal-property-info {
          padding: 14px 16px;
        }
        .modal-property-address {
          font-size: 0.95rem;
          font-weight: 600;
          color: #111;
          margin-bottom: 6px;
          letter-spacing: -0.005em;
        }
        .modal-property-price {
          font-size: 1.1rem;
          font-weight: 700;
          color: #047857;
          margin-bottom: 8px;
        }
        .modal-property-specs {
          font-size: 0.85rem;
          color: #374151;
          margin-bottom: 6px;
        }
        .modal-property-neighborhood {
          font-size: 0.8rem;
          color: #6b7280;
          font-style: italic;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 24px;
          border-top: 1px solid #f0f0f0;
        }
        .modal-empty {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }
        .btn-secondary {
          background-color: #ffffff;
          color: #111;
          border: 1px solid #e5e7eb;
          padding: 9px 18px;
          border-radius: 9px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.12s;
        }
        .btn-secondary:hover {
          background-color: #f9fafb;
          border-color: #d1d5db;
        }

        /* Activity summary rows */
        .activity-summary {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .activity-stat {
          font-size: 0.85rem;
          color: #374151;
        }
        .activity-stat strong {
          color: #111;
        }

        /* Empty state */
        .empty-icon {
          color: #9ca3af;
          margin-bottom: 16px;
        }

        /* Small buttons */
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .resend-btn {
          white-space: nowrap;
        }

        /* Tiny spinner — light */
        .loading-spinner-sm {
          width: 14px;
          height: 14px;
          border: 2px solid #f0f0f0;
          border-top: 2px solid #111;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        /* Form placeholder */
        .form-control::placeholder {
          color: #9ca3af;
          font-family: inherit;
          font-size: 0.9rem;
          opacity: 1;
        }

        /* Form layout */
        .form-row-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .form-row-grid .form-group:first-child {
          grid-column: 1 / -1;
        }
        .form-section {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 22px 24px;
          margin-bottom: 16px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .form-section .section-title {
          margin-bottom: 1.25rem;
        }
        .form-actions-right {
          display: flex;
          justify-content: flex-end;
          margin-top: 1.25rem;
        }

        /* Showings count helper */
        .showings-count {
          font-size: 0.9rem;
          color: #374151;
        }
        .showings-count strong {
          color: #111;
          font-size: 1.05rem;
        }

        @media (max-width: 768px) {
          .form-row-grid {
            grid-template-columns: 1fr;
          }
          .form-row-grid .form-group:first-child {
            grid-column: 1;
          }
          .form-section {
            padding: 1.25rem;
          }
        }
      `}</style>

      {/* Agent Messaging Modal */}
      {showMessageModal && selectedUserForMessage && (
        <div className="modal-overlay agent-messaging-modal">
          <div className="modal-content agent-messaging-content" style={{ 
            width: '700px', 
            maxWidth: '95vw', 
            height: '80vh', 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
            overflow: 'hidden'
          }}>
            <div className="modal-header agent-messaging-header" style={{ 
              backgroundColor: '#ffffff',
              borderBottom: '1px solid #f0f0f0',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 className="agent-messaging-title" style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#111', letterSpacing: '-0.01em' }}>
                Chat with {selectedUserForMessage.name}
              </h3>
              <button 
                onClick={closeMessageModal} 
                className="modal-close"
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f4f4f5';
                  e.target.style.color = '#111';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >×</button>
            </div>
            <div className="modal-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
              {/* Messages Area */}
              <div className="agent-messaging-area" style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                backgroundColor: '#ffffff'
              }}>
                {messages.length === 0 ? (
                  <div className="agent-messaging-empty" style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                    fontSize: '0.95rem'
                  }}>
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const isAgent = msg.senderRole === 'agent';
                    const showDate = index === 0 || 
                      new Date(messages[index - 1].createdAt).toDateString() !== 
                      new Date(msg.createdAt).toDateString();

                    return (
                      <React.Fragment key={msg._id}>
                        {showDate && (
                          <div className="agent-messaging-date" style={{
                            textAlign: 'center',
                            margin: '20px 0',
                            fontSize: '0.85rem',
                            color: '#9ca3af'
                          }}>
                            {new Date(msg.createdAt).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        )}
                        
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: isAgent ? 'flex-end' : 'flex-start',
                            gap: '12px'
                          }}
                        >
                          {!isAgent && (
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${selectedUserForMessage.profileImage ? 'transparent' : '#4a4a4a'}, ${selectedUserForMessage.profileImage ? 'transparent' : '#5a5a5a'})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0
                            }}>
                              {selectedUserForMessage.profileImage ? (
                                <img
                                  src={selectedUserForMessage.profileImage}
                                  alt={selectedUserForMessage.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{ color: 'white', fontSize: '0.9rem', fontWeight: '600' }}>
                                  {selectedUserForMessage.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                          )}
                          <div
                            className={`agent-messaging-bubble ${isAgent ? 'agent-sent' : 'user-sent'}`}
                            style={{
                              maxWidth: '70%',
                              padding: '10px 14px',
                              borderRadius: '16px',
                              background: isAgent ? '#111' : '#f4f4f5',
                              border: isAgent ? '1px solid #111' : '1px solid #e5e7eb',
                              color: isAgent ? '#ffffff' : '#111',
                              fontSize: '0.92rem',
                              lineHeight: '1.45',
                              textAlign: 'left'
                            }}
                          >
                            <div>{msg.content}</div>
                            <div style={{
                              fontSize: '0.75rem',
                              marginTop: '6px',
                              opacity: 0.6,
                              textAlign: isAgent ? 'right' : 'left'
                            }}>
                              {new Date(msg.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          {isAgent && (
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, ${agentInfo?.profileImage ? 'transparent' : '#4a4a4a'}, ${agentInfo?.profileImage ? 'transparent' : '#5a5a5a'})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0
                            }}>
                              {agentInfo?.profileImage ? (
                                <img
                                  src={agentInfo.profileImage}
                                  alt={agentInfo.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{ color: 'white', fontSize: '0.9rem', fontWeight: '600' }}>
                                  {agentInfo?.name?.charAt(0).toUpperCase() || 'A'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="agent-messaging-input-area" style={{
                padding: '20px',
                borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
                backgroundColor: '#ffffff'
              }}>
                <textarea
                  className="agent-messaging-textarea"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    background: '#ffffff',
                    color: '#111',
                    fontSize: '0.92rem',
                    resize: 'none',
                    minHeight: '44px',
                    maxHeight: '120px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.12s, box-shadow 0.12s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#111';
                    e.target.style.boxShadow = '0 0 0 3px rgba(17, 17, 17, 0.06)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                  rows={1}
                />
                <button
                  onClick={sendMessage}
                  className="btn btn-primary agent-messaging-send-btn"
                  disabled={isSending || !messageText.trim()}
                  style={{ 
                    alignSelf: 'flex-end',
                    backgroundColor: '#111',
                    border: '1px solid #111',
                    color: '#ffffff',
                    padding: '10px 22px',
                    borderRadius: '10px',
                    fontWeight: '600',
                    fontSize: '0.88rem',
                    cursor: isSending || !messageText.trim() ? 'not-allowed' : 'pointer',
                    opacity: isSending || !messageText.trim() ? 0.45 : 1,
                    transition: 'all 0.12s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSending && messageText.trim()) {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#111';
                    e.target.style.transform = 'none';
                  }}
                >
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
            <div className="modal-footer agent-messaging-footer" style={{ 
              backgroundColor: '#ffffff',
              borderTop: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <button 
                onClick={closeMessageModal} 
                className="btn btn-secondary agent-messaging-close-btn"
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  color: '#111',
                  padding: '9px 20px',
                  borderRadius: '9px',
                  fontWeight: '600',
                  fontSize: '0.88rem',
                  cursor: 'pointer',
                  transition: 'all 0.12s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Curate Matches Modal — extracted to its own component */}
      <CurateMatchesModal
        isOpen={showCurateModal}
        onClose={closeCurateModal}
        buyer={selectedUserForCurate}
        onPropertyDetailsClick={openPropertyDetails}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setTimeout(() => setSuccessMessage(''), 3000);
        }}
        onError={(msg) => {
          setError(msg);
          setTimeout(() => setError(''), 3000);
        }}
      />


      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div 
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div 
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '480px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #f1f5f9'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#0f172a',
                margin: 0
              }}>
                {showCropper ? 'Crop Your Photo' : 'Update Profile Photo'}
              </h3>
              <button 
                onClick={closePhotoModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {photoError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fee2e2',
                  borderRadius: '8px',
                  color: '#b91c1c',
                  fontSize: '14px',
                  marginBottom: '20px'
                }}>
                  {photoError}
                </div>
              )}
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              {showCropper ? (
                /* Cropper View */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '300px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <Cropper
                      image={imageToCrop}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  
                  <div style={{ padding: '8px 0' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '0 8px'
                    }}>
                      <ZoomOut size={18} style={{ color: '#64748b', flexShrink: 0 }} />
                      <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          outline: 'none',
                          cursor: 'pointer',
                          accentColor: '#4a4a4a'
                        }}
                      />
                      <ZoomIn size={18} style={{ color: '#64748b', flexShrink: 0 }} />
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
                    <button 
                      type="button" 
                      onClick={handleCropCancel}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#475569',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Back
                    </button>
                    <button 
                      onClick={handleCropConfirm}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#4a4a4a',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#1a1a1a',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Crop Photo
                    </button>
                  </div>
                </div>
              ) : !photoPreview ? (
                /* Drop Zone */
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  style={{
                    border: `2px dashed ${isDragging ? '#4a4a4a' : '#d1d5db'}`,
                    borderRadius: '12px',
                    padding: '48px 24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragging ? '#f9fafb' : '#fafafa',
                    transition: 'all 0.2s ease',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Upload size={48} style={{ color: '#9ca3af', marginBottom: '16px' }} />
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    margin: '0 0 8px 0'
                  }}>
                    Drag & drop your photo here
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 12px 0'
                  }}>
                    or click to browse
                  </p>
                  <p style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    margin: 0
                  }}>
                    JPG, PNG up to 10MB
                  </p>
                </div>
              ) : (
                /* Preview */
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <img 
                    src={photoPreview.dataUrl} 
                    alt="Preview" 
                    style={{
                      width: '200px',
                      height: '200px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '4px solid #f1f5f9'
                    }}
                  />
                  <button 
                    onClick={() => {
                      setPhotoPreview(null);
                      fileInputRef.current?.click();
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#475569',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Choose different photo
                  </button>
                </div>
              )}
              
              {/* Action Buttons */}
              {!showCropper && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: '12px', 
                  marginTop: '24px',
                  paddingTop: '20px',
                  borderTop: '1px solid #f1f5f9'
                }}>
                  <button 
                    type="button" 
                    onClick={closePhotoModal}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: 'transparent',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      color: '#475569',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handlePhotoUpload}
                    disabled={!photoPreview || isUploadingPhoto}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: !photoPreview || isUploadingPhoto ? '#d1d5db' : '#4a4a4a',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#1a1a1a',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: !photoPreview || isUploadingPhoto ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isUploadingPhoto ? (
                      <>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #d1d5db',
                          borderTopColor: '#ffffff',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite'
                        }}></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Save Photo
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BuyerDetailsModal
        buyer={selectedBuyerForDetails}
        isOpen={showBuyerDetailsModal}
        onClose={() => {
          setShowBuyerDetailsModal(false);
          setSelectedBuyerForDetails(null);
        }}
        onMessage={(buyer) => {
          setShowBuyerDetailsModal(false);
          setSelectedBuyerForDetails(null);
          openMessageModal(buyer);
        }}
        onCurateMatch={(buyer) => {
          setShowBuyerDetailsModal(false);
          setSelectedBuyerForDetails(null);
          openCurateModal(buyer);
        }}
      />
    </>
  );
};

export default AgentInvitations;